import { useState, useCallback, useMemo, useEffect } from 'react';
import type { Option, Section, DefaultOption, SelectedOption } from '../types';
import { calculateTotalPrice, calculateProjectDeadline } from '../utils/calculations';

export const useCalculator = (sections: Section[], defaultOptions: DefaultOption[] = [], options: Option[] = []) => {
  // Use a Map to store selected options with their quantities
  const [selectedOptionsMap, setSelectedOptionsMap] = useState<Map<string, SelectedOption>>(new Map());

  // Convert map to array for backward compatibility
  const selectedOptions = useMemo(() => {
    const options: Option[] = [];
    selectedOptionsMap.forEach((selected) => {
      // Add the option multiple times based on quantity for backward compatibility
      for (let i = 0; i < selected.quantity; i++) {
        options.push(selected.option);
      }
    });
    return options;
  }, [selectedOptionsMap]);

  // Get selected options with quantities for new functionality
  const selectedOptionsWithQuantities = useMemo(() => {
    return Array.from(selectedOptionsMap.values());
  }, [selectedOptionsMap]);

  // Create a combined array for calculations including the default options
  const allOptionsForCalculation = useMemo(() => {
    // Ensure defaultOptions is an array before using it
    if (!defaultOptions || !Array.isArray(defaultOptions) || defaultOptions.length === 0) {
      return selectedOptions;
    }

    // Convert DefaultOptions to Option format for calculations
    const defaultAsOptions: Option[] = defaultOptions.map(defOpt => ({
      id: defOpt.id,
      section_id: 'default',
      title: defOpt.title,
      description: defOpt.description || { ru: '', en: '', kz: '' },
      price: defOpt.price, // This can be undefined now
      days_till_completed: defOpt.days_till_completed,
    }));

    return [...defaultAsOptions, ...selectedOptions];
  }, [selectedOptions, defaultOptions]);

  // Separate fixed-price and hourly options
  const fixedPriceOptions = useMemo(
    () => allOptionsForCalculation.filter(opt => !opt.is_per_hour),
    [allOptionsForCalculation]
  );

  const hourlyOptions = useMemo(
    () => selectedOptions.filter(opt => opt.is_per_hour),
    [selectedOptions]
  );

  // Calculate total price for fixed-price options only (all currencies)
  const totalPrices = useMemo(
    () => ({
      us: calculateTotalPrice(fixedPriceOptions, 'us'),
      eu: calculateTotalPrice(fixedPriceOptions, 'eu'),
      kzt: calculateTotalPrice(fixedPriceOptions, 'kzt'),
      rub: calculateTotalPrice(fixedPriceOptions, 'rub'),
    }),
    [fixedPriceOptions]
  );

  // Calculate project deadline (only from fixed-price options, excluding hourly)
  const projectDeadline = useMemo(
    () => calculateProjectDeadline(fixedPriceOptions),
    [fixedPriceOptions]
  );

  // Clear saved selections from localStorage on mount (fresh start on every page load)
  useEffect(() => {
    // Clear all selection-related items from localStorage
    localStorage.removeItem('selectedOptionsWithQuantity');
    localStorage.removeItem('selectedOptions');
    // Start with empty selections
    setSelectedOptionsMap(new Map());
  }, []);

  // Save selections to localStorage whenever they change
  useEffect(() => {
    const toSave = Array.from(selectedOptionsMap.values());
    localStorage.setItem('selectedOptionsWithQuantity', JSON.stringify(toSave));
    // Also save in old format for backward compatibility
    localStorage.setItem('selectedOptions', JSON.stringify(selectedOptions));
  }, [selectedOptionsMap, selectedOptions]);

  // Auto-select options with min_instances > 0 when not already selected
  useEffect(() => {
    if (options.length === 0) return; // Wait for options to load

    setSelectedOptionsMap((prev) => {
      const newMap = new Map(prev);
      let hasChanges = false;

      options.forEach((option) => {
        // Check if option should be auto-selected
        if (
          option.allow_multiple_instances &&
          option.min_instances &&
          option.min_instances > 0 &&
          !newMap.has(option.id) // Not already selected
        ) {
          // Auto-select with minimum quantity (ignore options_num constraints)
          newMap.set(option.id, {
            option,
            quantity: option.min_instances,
          });
          hasChanges = true;
        }
      });

      return hasChanges ? newMap : prev;
    });
  }, [options]);

  const handleOptionToggle = useCallback((option: Option) => {
    setSelectedOptionsMap((prev) => {
      const newMap = new Map(prev);
      const isSelected = newMap.has(option.id);

      if (isSelected) {
        // Remove the option
        newMap.delete(option.id);
        return newMap;
      } else {
        // Check if we can add this option based on section constraints
        const section = sections.find(s => s.id === option.section_id);
        if (!section) return prev;

        // Get current section options
        const sectionOptions: Option[] = [];
        newMap.forEach((selected) => {
          if (selected.option.section_id === option.section_id) {
            sectionOptions.push(selected.option);
          }
        });

        // If options_num is undefined, allow unlimited selections
        if (section.options_num === undefined) {
          newMap.set(option.id, { option, quantity: 1 });
          return newMap;
        }

        if (section.options_num === 1) {
          // Radio button behavior - remove other selections from this section
          const toRemove: string[] = [];
          newMap.forEach((selected, id) => {
            if (selected.option.section_id === option.section_id) {
              toRemove.push(id);
            }
          });
          toRemove.forEach(id => newMap.delete(id));
          newMap.set(option.id, { option, quantity: 1 });
          return newMap;
        } else if (sectionOptions.length < section.options_num) {
          // Checkbox behavior - add if under limit
          newMap.set(option.id, { option, quantity: 1 });
          return newMap;
        }

        // Cannot add more options to this section
        return prev;
      }
    });
  }, [sections]);

  // Update quantity for an option
  const updateOptionQuantity = useCallback((optionId: string, quantity: number) => {
    setSelectedOptionsMap((prev) => {
      const newMap = new Map(prev);
      const selected = newMap.get(optionId);

      if (!selected) return prev;

      const option = selected.option;
      const minInstances = option.min_instances ?? 0;
      const maxInstances = option.max_instances;

      // If quantity is 0 or below minimum, remove the option (deselect)
      if (quantity <= 0 || quantity < minInstances) {
        newMap.delete(optionId);
        return newMap;
      }

      // Clamp to max if specified
      let validQuantity = quantity;
      if (maxInstances) {
        validQuantity = Math.min(maxInstances, validQuantity);
      }

      newMap.set(optionId, { ...selected, quantity: validQuantity });
      return newMap;
    });
  }, []);

  // Get quantity for a specific option
  const getOptionQuantity = useCallback((optionId: string): number => {
    const selected = selectedOptionsMap.get(optionId);
    return selected ? selected.quantity : 0;
  }, [selectedOptionsMap]);

  // Check if an option is selected
  const isOptionSelected = useCallback((optionId: string): boolean => {
    return selectedOptionsMap.has(optionId);
  }, [selectedOptionsMap]);

  const clearSelections = useCallback(() => {
    setSelectedOptionsMap(new Map());
    localStorage.removeItem('selectedOptions');
    localStorage.removeItem('selectedOptionsWithQuantity');
  }, []);

  const validateSelections = useCallback(() => {
    const missingSectionIds: string[] = [];
    const missingSections: Section[] = [];

    sections.forEach(section => {
      if (!section.optional) {
        let hasSectionOptions = false;
        selectedOptionsMap.forEach((selected) => {
          if (selected.option.section_id === section.id) {
            hasSectionOptions = true;
          }
        });

        if (!hasSectionOptions) {
          missingSectionIds.push(section.id);
          missingSections.push(section);
        }
      }
    });

    return {
      isValid: missingSectionIds.length === 0,
      missingSectionIds,
      missingSections
    };
  }, [sections, selectedOptionsMap]);

  return {
    selectedOptions,
    selectedOptionsWithQuantities,
    defaultOptions,
    totalPrices,
    projectDeadline,
    hourlyOptions,
    handleOptionToggle,
    updateOptionQuantity,
    getOptionQuantity,
    isOptionSelected,
    clearSelections,
    validateSelections
  };
};